import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './PosterCarousel.css';

const importAll = (r) => r.keys().map(r);
const posters = importAll(require.context('./assets/posters', false, /\.(png|jpe?g|svg|jfif)$/));

const PosterCarousel = () => {
  const [selectedPoster, setSelectedPoster] = useState(null);
  
  // Core refs for DOM manipulation and position tracking
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const positionRef = useRef(0);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(null);
  
  // Interaction state
  const [isGrabbing, setIsGrabbing] = useState(false);
  const grabStartRef = useRef(0);
  const grabPositionRef = useRef(0);
  const velocityRef = useRef(0);
  const lastPositionRef = useRef(0);
  const lastMoveTimeRef = useRef(0);
  const autoScrollRef = useRef(true);
  const isMobileRef = useRef(false);
  
  // Settings - wrapped in useMemo to avoid recreating on every render
  const settings = useMemo(() => ({
    autoScrollSpeed: 0.1,            // Slower speed
    itemWidth: 200,                  // Smaller width for each poster
    itemSpacing: 15,                 // Spacing between items
    cloneCount: 8,                   // Number of clones to ensure no blank spaces
    dragMomentumDecay: 0.95,         // How quickly momentum slows down (0.9-0.99)
    momentumThreshold: 0.1,          // When momentum becomes too small to continue
    repositionThreshold: 0.4,        // What fraction of poster width triggers repositioning during manual scroll
    transitionDuration: 300,         // ms - duration of smooth transitions
    autoScrollResumeDelay: 2000,     // ms - time before auto-scroll resumes after interaction
    mobileItemWidth: 140,            // Width on mobile devices
    mobileItemSpacing: 15            // Spacing on mobile devices
  }), []);
  
  // Detect if on mobile and set appropriate item width
  useEffect(() => {
    const checkIfMobile = () => {
      const isMobile = window.innerWidth <= 768;
      isMobileRef.current = isMobile;
    };
    
    // Check initially
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Calculate total width of original posters including spacing
  const totalOriginalWidth = useMemo(() => {
    const itemWidth = isMobileRef.current ? settings.mobileItemWidth : settings.itemWidth;
    const spacing = isMobileRef.current ? settings.mobileItemSpacing : settings.itemSpacing;
    return (itemWidth + spacing) * posters.length;
  }, [settings.itemWidth, settings.mobileItemWidth, settings.itemSpacing, settings.mobileItemSpacing]);
  
  // Apply transform with or without transition
  const applyTransform = useCallback((position, useTransition = true) => {
    if (!trackRef.current) return;
    
    if (!useTransition) {
      trackRef.current.style.transition = 'none';
    } else {
      trackRef.current.style.transition = `transform ${settings.transitionDuration}ms cubic-bezier(0.25, 1, 0.5, 1)`;
    }
    
    // Apply transform
    trackRef.current.style.transform = `translateX(${position}px)`;
    
    // Force reflow to ensure transition gets applied correctly
    if (!useTransition) {
      // eslint-disable-next-line no-unused-expressions
      trackRef.current.offsetHeight;
      trackRef.current.style.transition = `transform ${settings.transitionDuration}ms cubic-bezier(0.25, 1, 0.5, 1)`;
    }
  }, [settings.transitionDuration]);
  
  // Silently reposition when out of bounds to create infinite loop
  const checkAndRepositionIfNeeded = useCallback(() => {
    if (!trackRef.current) return false;
    
    // Recalculate width based on current device
    const itemWidth = isMobileRef.current ? settings.mobileItemWidth : settings.itemWidth;
    const spacing = isMobileRef.current ? settings.mobileItemSpacing : settings.itemSpacing;
    const currentTotalWidth = (itemWidth + spacing) * posters.length;
    
    let wasRepositioned = false;
    
    // Calculate the middle clone index for reference positioning
    const middleCloneIndex = Math.floor(settings.cloneCount / 2);
    const basePosition = -currentTotalWidth * middleCloneIndex;
    
    // If scrolled too far right (past first clone)
    if (positionRef.current > basePosition + currentTotalWidth) {
      // Move back by one full set width
      positionRef.current -= currentTotalWidth;
      applyTransform(positionRef.current, false);
      wasRepositioned = true;
    }
    
    // If scrolled too far left (past last clone)
    if (positionRef.current < basePosition - currentTotalWidth) {
      // Move forward by one full set width
      positionRef.current += currentTotalWidth;
      applyTransform(positionRef.current, false);
      wasRepositioned = true;
    }
    
    return wasRepositioned;
  }, [settings.cloneCount, settings.itemWidth, settings.mobileItemWidth, settings.itemSpacing, settings.mobileItemSpacing, applyTransform]);
  
  // Mouse/Touch event handlers - MOVED BEFORE useEffect that references them
  const handleGrabStart = useCallback((clientX) => {
    setIsGrabbing(true);
    autoScrollRef.current = false;
    velocityRef.current = 0;
    
    // Record the starting position
    grabStartRef.current = clientX;
    grabPositionRef.current = positionRef.current;
    
    // Reset velocity tracking
    lastPositionRef.current = positionRef.current;
    lastMoveTimeRef.current = performance.now();
    
    // Disable transitions during grabbing for responsive feel
    if (trackRef.current) {
      trackRef.current.style.transition = 'none';
    }
  }, []);
  
  const handleGrabMove = useCallback((clientX) => {
    if (!isGrabbing) return;
    
    // Calculate how far we've moved
    const grabOffset = clientX - grabStartRef.current;
    
    // Update position based on grab distance
    const newPosition = grabPositionRef.current + grabOffset;
    positionRef.current = newPosition;
    
    // Calculate velocity for momentum
    const currentTime = performance.now();
    const timeDelta = currentTime - lastMoveTimeRef.current;
    
    if (timeDelta > 0) {
      // Calculate velocity in pixels per millisecond
      velocityRef.current = (positionRef.current - lastPositionRef.current) / timeDelta;
      
      // Update last position and time for next calculation
      lastPositionRef.current = positionRef.current;
      lastMoveTimeRef.current = currentTime;
    }
    
    // Apply the transform
    applyTransform(positionRef.current, false);
    
    // Regularly check for repositioning during dragging
    checkAndRepositionIfNeeded();
  }, [isGrabbing, applyTransform, checkAndRepositionIfNeeded]);
  
  const handleGrabEnd = useCallback(() => {
    setIsGrabbing(false);
    
    // Force check repositioning immediately on release
    checkAndRepositionIfNeeded();
    
    // Don't immediately resume auto-scrolling if we have momentum
    if (Math.abs(velocityRef.current) <= settings.momentumThreshold) {
      // Resume auto-scrolling after delay if no meaningful momentum
      setTimeout(() => {
        autoScrollRef.current = true;
      }, settings.autoScrollResumeDelay);
    }
    
    // Restore the transition for smooth deceleration
    if (trackRef.current) {
      trackRef.current.style.transition = `transform ${settings.transitionDuration}ms cubic-bezier(0.25, 1, 0.5, 1)`;
    }
  }, [settings.momentumThreshold, settings.autoScrollResumeDelay, settings.transitionDuration, checkAndRepositionIfNeeded]);
  
  // Main animation loop
  const animate = useCallback((timestamp) => {
    if (!lastTimeRef.current) {
      lastTimeRef.current = timestamp;
    }
    
    const deltaTime = timestamp - lastTimeRef.current;
    lastTimeRef.current = timestamp;
    
    // Handle auto-scrolling
    if (autoScrollRef.current && !isGrabbing) {
      positionRef.current -= settings.autoScrollSpeed * deltaTime;
      applyTransform(positionRef.current, false);
      
      // Check and silently reposition if needed
      checkAndRepositionIfNeeded();
    }
    
    // Handle momentum scrolling after manual interaction
    if (!isGrabbing && !autoScrollRef.current && Math.abs(velocityRef.current) > settings.momentumThreshold) {
      positionRef.current += velocityRef.current * deltaTime;
      applyTransform(positionRef.current, false);
      
      // Apply momentum decay
      velocityRef.current *= settings.dragMomentumDecay;
      
      // Check and silently reposition if needed
      checkAndRepositionIfNeeded();
      
      // Stop momentum when it gets too small
      if (Math.abs(velocityRef.current) <= settings.momentumThreshold) {
        velocityRef.current = 0;
        
        // Resume auto-scrolling after a delay
        setTimeout(() => {
          autoScrollRef.current = true;
        }, settings.autoScrollResumeDelay);
      }
    }
    
    animationRef.current = requestAnimationFrame(animate);
  }, [
    isGrabbing, 
    applyTransform, 
    checkAndRepositionIfNeeded, 
    settings.autoScrollSpeed,
    settings.momentumThreshold,
    settings.dragMomentumDecay,
    settings.autoScrollResumeDelay
  ]);
  
  // Update position when width changes (like on resize)
  useEffect(() => {
    // Recalculate total width based on current device
    const itemWidth = isMobileRef.current ? settings.mobileItemWidth : settings.itemWidth;
    const spacing = isMobileRef.current ? settings.mobileItemSpacing : settings.itemSpacing;
    const recalculatedTotalWidth = (itemWidth + spacing) * posters.length;
    
    // Initialize to the middle of the cloned sets to allow scrolling in both directions
    const middleCloneIndex = Math.floor(settings.cloneCount / 2);
    positionRef.current = -recalculatedTotalWidth * middleCloneIndex;
    
    if (trackRef.current) {
      applyTransform(positionRef.current, false);
    }
  }, [totalOriginalWidth, applyTransform, settings.cloneCount, settings.itemWidth, settings.mobileItemWidth, settings.itemSpacing, settings.mobileItemSpacing]);
  
  // Set up and clean up animation loop
  useEffect(() => {
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate]);
  
  // Set up touch events with proper passive options
  useEffect(() => {
    if (!trackRef.current) return;
    
    const track = trackRef.current;
    
    // Add touch event listeners directly with passive option
    const touchStartHandler = (e) => handleGrabStart(e.touches[0].clientX);
    const touchMoveHandler = (e) => handleGrabMove(e.touches[0].clientX);
    
    track.addEventListener('touchstart', touchStartHandler, { passive: true });
    track.addEventListener('touchmove', touchMoveHandler, { passive: true });
    track.addEventListener('touchend', handleGrabEnd, { passive: true });
    
    return () => {
      track.removeEventListener('touchstart', touchStartHandler);
      track.removeEventListener('touchmove', touchMoveHandler);
      track.removeEventListener('touchend', handleGrabEnd);
    };
  }, [handleGrabStart, handleGrabMove, handleGrabEnd]);
  
  // Mouse event handlers
  const handleMouseDown = (e) => {
    handleGrabStart(e.clientX);
    e.preventDefault(); // Prevent text selection during drag
  };
  
  const handleMouseMove = (e) => {
    handleGrabMove(e.clientX);
    if (isGrabbing) {
      e.preventDefault();
    }
  };
  
  const handleMouseUp = () => {
    handleGrabEnd();
  };
  
  const handleMouseLeave = () => {
    if (isGrabbing) {
      handleGrabEnd();
    }
  };
  
  // Prepare poster arrays for render
  const allPosterElements = useMemo(() => {
    const itemWidth = isMobileRef.current ? settings.mobileItemWidth : settings.itemWidth;
    const spacing = isMobileRef.current ? settings.mobileItemSpacing : settings.itemSpacing;
    
    return Array.from({ length: settings.cloneCount }).map((_, cloneIndex) => (
      <React.Fragment key={`clone-${cloneIndex}`}>
        {posters.map((poster, posterIndex) => (
          <div 
            key={`${cloneIndex}-${posterIndex}`}
            className="poster-item"
            style={{
              width: `${itemWidth}px`,
              flexShrink: 0,
              marginRight: `${spacing}px` // Add spacing between items
            }}
          >
            <img
              src={poster}
              alt={`Poster ${posterIndex + 1}`}
              className="poster-image"
              onClick={() => setSelectedPoster(poster)}
              draggable={false}
              style={{ 
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                userSelect: 'none',
                WebkitUserDrag: 'none'
              }}
            />
          </div>
        ))}
      </React.Fragment>
    ));
  }, [
    settings.cloneCount, 
    settings.itemWidth, 
    settings.mobileItemWidth, 
    settings.itemSpacing, 
    settings.mobileItemSpacing
  ]);
  
  return (
    <>
      <div 
        className="poster-carousel" 
        ref={containerRef}
      >
        <div 
          className="poster-track"
          ref={trackRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{ 
            display: 'flex',
            flexDirection: 'row'
          }}
        >
          {allPosterElements}
        </div>
      </div>

      {selectedPoster && (
        <div className="modal" onClick={() => setSelectedPoster(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedPoster} alt="Poster Large View" className="modal-image" />
            <button className="close-button" onClick={() => setSelectedPoster(null)}>✖</button>
          </div>
        </div>
      )}
    </>
  );
};

export default PosterCarousel;
